import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import SocialHeader from '../components/social/SocialHeader';
import FacebookPageAnalytics from '../components/social/FacebookPageAnalytics';
import InstagramAnalytics from '../components/social/InstagramAnalytics';
import LinkedInAnalytics from '../components/social/LinkedInAnalytics';
import { getStoredUserId } from '../utils/auth';
import { API_BASE_URL } from '../constants/apiConstants';
import './CSS/SocialMediaAutomation.css';

const connectDestinations = [
  {
    id: 'facebook',
    label: 'Connect Facebook',
    type: 'oauth',
    apiEndpoint: '/meta/auth/login-url',
    description: 'Connect Facebook Pages & Instagram'
  },
  {
    id: 'instagram',
    label: 'Connect Instagram',
    type: 'oauth',
    apiEndpoint: '/meta/auth/login-url',
    description: 'Uses same login as Facebook'
  },
  {
    id: 'linkedin',
    label: 'Connect LinkedIn',
    type: 'oauth',
    apiEndpoint: '/linkedin/auth/login-url',
    description: 'Connect your LinkedIn organization'
  },
  {
    id: 'pinterest',
    label: 'Connect Pinterest',
    type: 'direct',
    url: 'https://www.pinterest.com/login/',
    description: 'Coming soon'
  }
];

const SocialMediaAutomation = () => {
  const [notification, setNotification] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('facebook');
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [facebookPages, setFacebookPages] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [linkedinOrganizations, setLinkedInOrganizations] = useState([]);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  // Normalize Instagram accounts from API or embedded page data
  const normalizeInstagramAccounts = (igAccounts = [], fbPages = []) => {
    if (Array.isArray(igAccounts) && igAccounts.length > 0) {
      return igAccounts.map((account) => ({
        ...account,
        instagram_account_id: account.instagram_account_id || account.id,
        linked_facebook_page: account.linked_facebook_page || account.facebook_page_id || account.page_id
      }));
    }

    return fbPages
      .map((page) => {
        let instagram = page.instagram_business_account;
        if (typeof instagram === 'string') {
          try {
            instagram = JSON.parse(instagram);
          } catch (err) {
            instagram = null;
          }
        }

        if (instagram?.id) {
          return {
            instagram_account_id: instagram.id,
            username: instagram.username,
            followers_count: instagram.followers_count,
            follows_count: instagram.follows_count,
            media_count: instagram.media_count,
            biography: instagram.biography,
            website: instagram.website,
            linked_facebook_page: page.page_id
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  // Handle OAuth callback redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connection = params.get('connection');
    const platform = params.get('platform');
    const pages = params.get('pages');
    const instagram = params.get('instagram');
    const orgs = params.get('orgs');
    const message = params.get('message');
    const metaConnected = params.get('meta_connected');
    const errorParam = params.get('error');

    if (metaConnected === 'true') {
      setNotification({
        type: 'success',
        message: 'Successfully connected to Facebook & Instagram!'
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setNotification(null), 5000);
      checkConnectionStatus();
    } else if (connection === 'success') {
      const isLinkedIn = platform?.toLowerCase() === 'linkedin';
      const successMsg = isLinkedIn
        ? `Successfully connected to LinkedIn! ${orgs || 0} organization(s) found.`
        : `Successfully connected to ${platform}! ${pages || 0} Facebook page(s) and ${instagram || 0} Instagram account(s) found.`;
      setNotification({ type: 'success', message: successMsg });
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setNotification(null), 5000);
      checkConnectionStatus();
    } else if (connection === 'error' || errorParam) {
      setNotification({
        type: 'error',
        message: `Failed to connect: ${decodeURIComponent(message || errorParam || 'Unknown error')}`
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setNotification(null), 5000);
    }
  }, []);

  // Check connection status on mount
  const checkConnectionStatus = async () => {
    try {
      setIsCheckingConnection(true);
      const userid = getStoredUserId();
      if (!userid) {
        console.log('No user ID found, skipping connection status check');
        setIsCheckingConnection(false);
        return;
      }

      // Fetch Meta data
      const response = await axios.get(`${API_BASE_URL}/meta/data`, {
        headers: {
          'userid': userid
        }
      });

      if (response.data.success && response.data.data) {
        const { token, facebook_pages = [], instagram_accounts = [] } = response.data.data;

        setFacebookPages(facebook_pages);
        setInstagramAccounts(normalizeInstagramAccounts(instagram_accounts, facebook_pages));

        if (token) {
          setConnectedAccounts([
            {
              ...token,
              pages: facebook_pages
            }
          ]);
        } else {
          setConnectedAccounts([]);
        }
      }

      // Fetch LinkedIn organizations
      try {
        const linkedinResponse = await axios.get(`${API_BASE_URL}/linkedin/organizations`, {
          headers: {
            'userid': userid
          }
        });

        if (linkedinResponse.data.success && linkedinResponse.data.organizations) {
          setLinkedInOrganizations(linkedinResponse.data.organizations);
        }
      } catch (err) {
        console.log('LinkedIn organizations not available or not connected');
        setLinkedInOrganizations([]);
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const handleSocialConnect = async (destination) => {
    try {
      // Get userid from storage - required for authentication
      const userid = getStoredUserId();
      if (!userid) {
        alert('Authentication required. Please log in first.');
        return;
      }

      if (destination.type === 'oauth') {
        // OAuth flow - call BFF to get login URL
        // Include JSON-encoded state with userid so the callback can map the response to the user
        const stateObj = { userid };
        const state = JSON.stringify(stateObj);
        console.log('Calling API:', `${API_BASE_URL}${destination.apiEndpoint}`);

        const response = await axios.get(`${API_BASE_URL}${destination.apiEndpoint}`, {
          params: { state },
          headers: {
            'userid': userid
          }
        });

        console.log('API Response:', response.data);

        if (response.data.success && response.data.login_url) {
          // Redirect to OAuth provider
          console.log('Redirecting to:', response.data.login_url);
          window.location.href = response.data.login_url;
        } else {
          console.error('Failed to get login URL:', response.data);
          alert('Failed to initiate authentication. Please try again.');
        }
      } else {
        // Direct URL navigation (Pinterest, etc.)
        window.open(destination.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error connecting to social platform:', error);
      console.error('Error details:', error.response?.data);
      alert(`Failed to connect: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <div className="social-page">
      <Navbar />
      <main className="social-shell">
        {notification && (
          <div className={`notification notification-${notification.type}`} style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 24px',
            borderRadius: '8px',
            backgroundColor: notification.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxWidth: '400px',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>
                {notification.type === 'success' ? '✓' : '✕'}
              </span>
              <span>{notification.message}</span>
              <button
                onClick={() => setNotification(null)}
                style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '0',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
        <SocialHeader
          title="Social Media Management"
          subtitle="Plan, schedule, and track your social media posts"
          onCreatePost={() => window.alert('Create post flow coming soon')}
          onAutoSchedule={() => window.alert('Auto schedule coming soon')}
        />

        <section className="connected-panel">
          <div className="section-heading">
            <h2>Connected Accounts</h2>
            <p>Manage authentication across channels</p>
            {!isCheckingConnection && connectedAccounts.length > 0 && (
              <div className="connection-badge success">
                ✓ Connected to {connectedAccounts.length} account(s)
              </div>
            )}
          </div>

          {connectedAccounts.length > 0 && (
            <div className="connected-accounts-list">
              {connectedAccounts.map((account) => (
                <div key={account.facebook_user_id || account.page_id} className="account-item">
                  {account.facebook_picture && (
                    <img src={account.facebook_picture} alt={account.facebook_name} className="account-avatar" />
                  )}
                  <div className="account-details">
                    <h4>{account.facebook_name || account.page_name || 'Facebook Account'}</h4>
                    <p>{account.facebook_email || account.category || 'Connected via Meta'}</p>
                  </div>
                  <span className="connection-status">✓ Active</span>
                </div>
              ))}
            </div>
          )}

          {facebookPages.length > 0 && (
            <div className="connected-accounts-list" style={{ marginTop: '12px' }}>
              {facebookPages.map((page) => {
                let instagram = page.instagram_business_account;
                if (typeof instagram === 'string') {
                  try {
                    instagram = JSON.parse(instagram);
                  } catch (err) {
                    instagram = null;
                  }
                }

                return (
                  <div key={page.page_id} className="account-item">
                    <div className="account-details">
                      <h4>{page.page_name}</h4>
                      <p>{page.category || 'Facebook Page'}</p>
                      <p>Page ID: {page.page_id}</p>
                      {instagram?.username && (
                        <p>Linked Instagram: @{instagram.username}</p>
                      )}
                    </div>
                    <span className="connection-status">Facebook Page</span>
                  </div>
                );
              })}
            </div>
          )}

          {instagramAccounts.length > 0 && (
            <div className="connected-accounts-list" style={{ marginTop: '12px' }}>
              {instagramAccounts.map((account) => (
                <div key={account.instagram_account_id} className="account-item">
                  <div className="account-details">
                    <h4>@{account.username}</h4>
                    <p>{(account.followers_count || 0).toLocaleString()} followers • {(account.media_count || 0).toLocaleString()} posts</p>
                    {account.linked_facebook_page && (
                      <p>Linked Page: {account.linked_facebook_page}</p>
                    )}
                  </div>
                  <span className="connection-status">Instagram</span>
                </div>
              ))}
            </div>
          )}

          {linkedinOrganizations.length > 0 && (
            <div className="connected-accounts-list" style={{ marginTop: '12px' }}>
              {linkedinOrganizations.map((org) => (
                <div key={org.organization_id} className="account-item">
                  <div className="account-details">
                    <h4>{org.organization_name}</h4>
                    <p>{(org.follower_count || 0).toLocaleString()} followers</p>
                    {org.is_primary && (
                      <p style={{ color: '#0a66c2', fontWeight: '600' }}>📌 Primary Organization</p>
                    )}
                  </div>
                  <span className="connection-status">LinkedIn</span>
                </div>
              ))}
            </div>
          )}

          {/* Meta Integration Notice */}
          <div className="meta-integration-notice">
            <div className="notice-icon">ℹ️</div>
            <div className="notice-content">
              <strong>About Facebook & Instagram Connection:</strong>
              <p>Both Facebook and Instagram use the same Meta login. Connecting either one will authenticate your Facebook account. Instagram Business analytics will appear automatically if your Facebook Page has a linked Instagram Business account.</p>
            </div>
          </div>

          <div className="account-actions">
            {connectDestinations.map((destination) => (
              <button
                type="button"
                key={destination.id}
                className="connect-destination-btn"
                onClick={() => handleSocialConnect(destination)}
                title={destination.description}
              >
                <span className="btn-label">{destination.label}</span>
                {destination.description && (
                  <span className="btn-description">{destination.description}</span>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Platform Toggle for Analytics */}
        <section className="platform-toggle-section">
          <div className="platform-toggle-container">
            <h3>Performance Analytics</h3>
            <div className="platform-toggle">
              <button
                className={`platform-toggle-btn ${selectedPlatform === 'facebook' ? 'active' : ''}`}
                onClick={() => setSelectedPlatform('facebook')}
              >
                <span className="platform-icon">📘</span>
                Facebook
              </button>
              <button
                className={`platform-toggle-btn ${selectedPlatform === 'instagram' ? 'active' : ''}`}
                onClick={() => setSelectedPlatform('instagram')}
              >
                <span className="platform-icon">📸</span>
                Instagram
              </button>
              <button
                className={`platform-toggle-btn ${selectedPlatform === 'linkedin' ? 'active' : ''}`}
                onClick={() => setSelectedPlatform('linkedin')}
              >
                <span className="platform-icon">💼</span>
                LinkedIn
              </button>
            </div>
          </div>
        </section>

        {/* Keep all analytics panels mounted so state/data is preserved when switching platforms */}
        <section className="analytics-section" style={{ display: selectedPlatform === 'facebook' ? '' : 'none' }}>
          <FacebookPageAnalytics />
        </section>

        <section className="analytics-section" style={{ display: selectedPlatform === 'instagram' ? '' : 'none' }}>
          <InstagramAnalytics />
        </section>

        <section className="analytics-section" style={{ display: selectedPlatform === 'linkedin' ? '' : 'none' }}>
          <LinkedInAnalytics />
        </section>
      </main>
    </div>
  );
};

export default SocialMediaAutomation;
